import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Mail, Server, Globe, TestTube, Save, Eye, EyeOff, Info } from 'lucide-react';

const token = () => localStorage.getItem('token');

export default function EmailSettingsPage() {
  const [form, setForm] = useState({
    from_email: '', from_name: '',
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_ssl: false,
    imap_host: '', imap_port: 993, imap_username: '', imap_password: '', imap_ssl: true,
  });
  const [hasSmtpPwd, setHasSmtpPwd] = useState(false);
  const [hasImapPwd, setHasImapPwd] = useState(false);
  const [showSmtpPwd, setShowSmtpPwd] = useState(false);
  const [showImapPwd, setShowImapPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/email-settings`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => {
        const d = res.data;
        setForm(f => ({
          ...f,
          from_email: d.from_email || '',
          from_name: d.from_name || '',
          smtp_host: d.smtp_host || '',
          smtp_port: d.smtp_port || 587,
          smtp_username: d.smtp_username || '',
          smtp_ssl: d.smtp_ssl || false,
          imap_host: d.imap_host || '',
          imap_port: d.imap_port || 993,
          imap_username: d.imap_username || '',
          imap_ssl: d.imap_ssl !== false,
        }));
        setHasSmtpPwd(d.has_smtp_password || false);
        setHasImapPwd(d.has_imap_password || false);
        if (d.from_email && !testEmail) setTestEmail(d.from_email);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-settings`, form, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('E-posta ayarları kaydedildi');
      if (form.smtp_password) setHasSmtpPwd(true);
      if (form.imap_password) setHasImapPwd(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    if (!testEmail) { toast.error('Test e-posta adresi girin'); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/email-settings/test`, { to_email: testEmail }, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Test e-postası gönderildi!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bağlantı hatası');
    } finally { setTesting(false); }
  };

  const autoDetect = async () => {
    if (!form.from_email || !form.from_email.includes('@')) {
      toast.error('Önce geçerli bir e-posta adresi girin');
      return;
    }
    setAutoDetecting(true);
    try {
      const res = await axios.get(`${API}/email-settings/detect`, {
        params: { email: form.from_email },
        headers: { Authorization: `Bearer ${token()}` }
      });
      const d = res.data;
      setForm(f => ({
        ...f,
        smtp_host: d.smtp_host,
        smtp_port: d.smtp_port,
        smtp_ssl: d.smtp_ssl,
        smtp_username: d.smtp_username || f.smtp_username,
        imap_host: d.imap_host,
        imap_port: d.imap_port,
        imap_ssl: d.imap_ssl,
        imap_username: d.imap_username || f.imap_username,
      }));
      const msg = d.note
        ? `${d.provider} tespit edildi.\n⚠️ ${d.note}`
        : `${d.provider} tespit edildi, ayarlar dolduruldu`;
      toast.success(msg, { duration: d.note ? 6000 : 3000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Otomatik algılama başarısız');
    } finally {
      setAutoDetecting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <strong>Neden SMTP Ayarları?</strong><br />
          SMTP ayarlarını yapılandırarak, müşterilerinize kendi e-posta adresinizden mail gönderebilirsiniz.
          Gmail kullanıyorsanız <strong>Uygulama Şifresi</strong> (App Password) oluşturmanız gerekir.
        </div>
      </div>

      {/* E-posta Adresi */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-500" />
          <p className="text-sm font-semibold text-gray-700">E-posta Adresi</p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="space-y-1.5">
            <Label>E-posta Adresi</Label>
            <div className="flex gap-2">
              <Input value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))}
                placeholder="ornek@sirketiniz.com" className="flex-1" data-testid="smtp-from-email" />
              <Button variant="outline" onClick={autoDetect} disabled={autoDetecting} className="gap-1.5 shrink-0">
                <Server className="w-4 h-4" /> {autoDetecting ? 'Algılanıyor...' : 'Otomatik Algıla'}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Görünen İsim</Label>
            <Input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))}
              placeholder="Adınız Soyadınız - Şirketiniz" data-testid="smtp-from-name" />
            <p className="text-xs text-gray-400">Alıcıların göreceği isim</p>
          </div>
        </div>
      </div>

      {/* SMTP */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Server className="w-4 h-4 text-purple-500" />
          <p className="text-sm font-semibold text-gray-700">Giden Sunucu (SMTP)</p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>SMTP Sunucu</Label>
              <Input value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))}
                placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div className="space-y-1.5">
              <Label>Port</Label>
              <Input type="number" value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: Number(e.target.value) }))}
                data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kullanıcı Adı</Label>
              <Input value={form.smtp_username} onChange={e => setForm(f => ({ ...f, smtp_username: e.target.value }))}
                placeholder="ornek@sirketiniz.com" data-testid="smtp-username" />
            </div>
            <div className="space-y-1.5">
              <Label>Şifre {hasSmtpPwd && !form.smtp_password && <span className="text-xs text-green-600 ml-1">(kayıtlı)</span>}</Label>
              <div className="relative">
                <Input type={showSmtpPwd ? 'text' : 'password'} value={form.smtp_password}
                  onChange={e => setForm(f => ({ ...f, smtp_password: e.target.value }))}
                  placeholder={hasSmtpPwd ? '••••••••• (değiştirmek için girin)' : 'Şifreniz'}
                  data-testid="smtp-password" className="pr-10" />
                <button type="button" onClick={() => setShowSmtpPwd(s => !s)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                  {showSmtpPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400">Gmail için uygulama şifresi kullanın</p>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 block mb-2">Şifreleme</Label>
            <div className="flex gap-4">
              {[{ val: false, label: 'TLS (Port 587)' }, { val: true, label: 'SSL (Port 465)' }].map(opt => (
                <label key={String(opt.val)} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" checked={form.smtp_ssl === opt.val}
                    onChange={() => setForm(f => ({ ...f, smtp_ssl: opt.val, smtp_port: opt.val ? 465 : 587 }))}
                    className="accent-teal-600" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* IMAP */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Globe className="w-4 h-4 text-green-500" />
          <p className="text-sm font-semibold text-gray-700">Gelen Sunucu (IMAP) — Opsiyonel</p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>IMAP Sunucu</Label>
              <Input value={form.imap_host} onChange={e => setForm(f => ({ ...f, imap_host: e.target.value }))}
                placeholder="imap.gmail.com" data-testid="imap-host" />
            </div>
            <div className="space-y-1.5">
              <Label>Port</Label>
              <Input type="number" value={form.imap_port} onChange={e => setForm(f => ({ ...f, imap_port: Number(e.target.value) }))}
                data-testid="imap-port" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kullanıcı Adı</Label>
              <Input value={form.imap_username} onChange={e => setForm(f => ({ ...f, imap_username: e.target.value }))}
                placeholder="ornek@sirketiniz.com" data-testid="imap-username" />
            </div>
            <div className="space-y-1.5">
              <Label>Şifre {hasImapPwd && !form.imap_password && <span className="text-xs text-green-600 ml-1">(kayıtlı)</span>}</Label>
              <div className="relative">
                <Input type={showImapPwd ? 'text' : 'password'} value={form.imap_password}
                  onChange={e => setForm(f => ({ ...f, imap_password: e.target.value }))}
                  placeholder={hasImapPwd ? '••••••••• (değiştirmek için girin)' : 'Şifreniz'}
                  data-testid="imap-password" className="pr-10" />
                <button type="button" onClick={() => setShowImapPwd(s => !s)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                  {showImapPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Test */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <TestTube className="w-4 h-4 text-orange-500" />
          <p className="text-sm font-semibold text-gray-700">Bağlantı Testi</p>
        </div>
        <div className="px-4 py-4">
          <p className="text-sm text-gray-500 mb-3">E-posta göndermeden önce SMTP bağlantısını test edin</p>
          <div className="flex gap-2">
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="test@ornek.com" className="flex-1" data-testid="test-email-input" />
            <Button variant="outline" onClick={testConnection} disabled={testing} className="gap-1.5 shrink-0" data-testid="test-connection-btn">
              <TestTube className="w-4 h-4" /> {testing ? 'Gönderiliyor...' : 'Bağlantıyı Test Et'}
            </Button>
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="save-email-settings-btn">
        <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </Button>
    </div>
  );
}
